/**
 * P3-005: Generated router — creates React Router routes from the route registry.
 *
 * App.tsx mounts this router. No product page imports in App.tsx.
 * Route components are resolved from canonical contracts and the reviewed
 * effective bespoke renderer registry.
 */
import {
  createBrowserRouter,
  generatePath,
  Navigate,
  useLocation,
  useParams,
  type RouteObject,
} from 'react-router-dom';
import type { ReactNode } from 'react';
import { lazy, Suspense } from 'react';
import { ApplicationShell } from '../../components/shell';
import { AuthLayout } from '../../layouts/AuthLayout';
import { PageLoadingState, PageNotFoundState } from '../../components/states';
import { PageRenderer } from '../../components/experience/PageRenderer';
import { useSession } from '../../features/auth/session-provider';
import { webRouteRegistry } from '../../routes/registry';
import { isBespokeRoute, getBespokeEntry } from '../../runtime/pages/effective-bespoke-registry';
import type { WebRouteEntry } from '../../routes/registry';
import {
  getPageByRouteId,
  getRouteById,
  getHubDefinition,
  getHubResourceByCreateRoute,
  getHubResourceByDetailRoute,
  getHubResourceByEditRoute,
  getHubResourceByListRoute,
  ROUTE_IDS,
} from '@finance/contracts';
import type { PageDescriptor } from '@finance/contracts';
import { OperationRouteGuard } from '../../features/auth/OperationRouteGuard';
import { HubRoutePage } from '../../components/experience/HubRoutePage';
import { ResourceRoutePage } from '../../components/experience/ResourceRoutePage';
import { PageTransition } from '../../components/layout/PageTransition';

const bespokeCache = new Map<string, React.LazyExoticComponent<React.ComponentType>>();

function getBespokeComponent(routeId: string): React.LazyExoticComponent<React.ComponentType> | null {
  if (!isBespokeRoute(routeId)) return null;
  if (bespokeCache.has(routeId)) return bespokeCache.get(routeId)!;

  const entry = getBespokeEntry(routeId);
  if (!entry) return null;

  const LazyComp = lazy(entry.component);
  bespokeCache.set(routeId, LazyComp);
  return LazyComp;
}

function isHubRoute(routeId: WebRouteEntry['routeId']): boolean {
  return getHubDefinition(routeId) != null;
}

function isGenericResourceRoute(routeId: WebRouteEntry['routeId']): boolean {
  if (isBespokeRoute(routeId)) return false;
  return (
    getHubResourceByListRoute(routeId) != null ||
    getHubResourceByDetailRoute(routeId) != null ||
    getHubResourceByCreateRoute(routeId) != null ||
    getHubResourceByEditRoute(routeId) != null
  );
}

function PrivateRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading, restorationState } = useSession();
  const location = useLocation();

  if (loading || restorationState === 'idle' || restorationState === 'restoring') {
    return <PageLoadingState />;
  }
  if (!isAuthenticated) {
    const loginPath = getRouteById(ROUTE_IDS.LOGIN)?.path ?? '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function RedirectRouteElement({ route }: { route: WebRouteEntry }) {
  const sourceParams = useParams();
  const target = route.redirectRouteId
    ? webRouteRegistry.find(candidate => candidate.routeId === route.redirectRouteId)
    : undefined;
  if (!target) return <PageNotFoundState />;

  const sourceValues = Object.values(sourceParams).filter(
    (value): value is string => typeof value === 'string'
  );
  let parameterIndex = 0;
  const targetParams = Object.fromEntries(
    [...target.path.matchAll(/:([^/]+)/g)].map(match => [
      match[1],
      sourceParams[match[1]] ?? sourceValues[parameterIndex++],
    ])
  );
  if (Object.values(targetParams).some(value => !value)) return <PageNotFoundState />;

  return <Navigate to={generatePath(target.path, targetParams)} replace />;
}

function resolvePageDescriptor(route: WebRouteEntry): PageDescriptor {
  const page = getPageByRouteId(route.routeId);
  if (page) {
    if (!isBespokeRoute(route.routeId) && page.sections.length === 0) {
      if (isHubRoute(route.routeId) || isGenericResourceRoute(route.routeId)) {
        return page;
      }
      throw new Error(
        `Route ${route.routeId} (pageId ${route.pageId}) has an incomplete page descriptor ` +
          'with no sections. Either add sections to the descriptor or register the route as bespoke.',
      );
    }
    return page;
  }

  if (isHubRoute(route.routeId) || isGenericResourceRoute(route.routeId)) {
    return { id: route.pageId, titleKey: '' as never, sections: [] } as unknown as PageDescriptor;
  }

  if (getBespokeEntry(route.routeId)) {
    throw new Error(
      `Bespoke route ${route.routeId} is missing page metadata; ` +
        'add canonical page metadata or reconcile the renderer registry.',
    );
  }

  throw new Error(
    `Route ${route.routeId} (pageId ${route.pageId}) has no registered page descriptor ` +
      'and is not in the bespoke renderer registry.',
  );
}

function RouteElement({ route }: { route: WebRouteEntry }) {
  const { loading } = useSession();
  if (loading) return <PageLoadingState />;

  if (isHubRoute(route.routeId)) {
    return (
      <PageTransition>
        <HubRoutePage hubRouteId={route.routeId} />
      </PageTransition>
    );
  }

  const bespoke = getBespokeComponent(route.routeId);
  if (bespoke) {
    const BespokeComponent = bespoke;
    return (
      <PageTransition>
        <Suspense fallback={<PageLoadingState />}>
          <BespokeComponent />
        </Suspense>
      </PageTransition>
    );
  }

  if (isGenericResourceRoute(route.routeId)) {
    return (
      <PageTransition>
        <ResourceRoutePage routeId={route.routeId} />
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <PageRenderer contract={resolvePageDescriptor(route)} loading={false} />
    </PageTransition>
  );
}

export function createRouter() {
  const authRoutes = webRouteRegistry.filter((route) => route.layoutId === 'auth');
  const appRoutes = webRouteRegistry.filter((route) => route.layoutId === 'app');
  const dashboardPath = getRouteById(ROUTE_IDS.DASHBOARD)?.path ?? '/dashboard';

  const routes: RouteObject[] = [
    { path: '/', element: <Navigate to={dashboardPath} replace /> },
    {
      element: <AuthLayout />,
      children: authRoutes.map((route) => ({
        path: route.path,
        element: <RouteElement route={route} />,
      })),
    },
    {
      element: (
        <PrivateRoute>
          <ApplicationShell />
        </PrivateRoute>
      ),
      children: appRoutes.map((route) => ({
        path: route.path,
        element: route.availability === 'REDIRECT' ? (
          <RedirectRouteElement route={route} />
        ) : (
          <OperationRouteGuard routeId={route.routeId}>
            <RouteElement route={route} />
          </OperationRouteGuard>
        ),
      })),
    },
    { path: '*', element: <PageNotFoundState /> },
  ];

  return createBrowserRouter(routes);
}
